/**
 * Student auth context.
 *
 * Stores the JWT in sessionStorage (matches the existing admin pattern) so
 * the rest of the app can look it up via `useStudentAuth()`. On mount,
 * if a token exists, we load /students/me to check it's still valid.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const TOKEN_KEY = "student_token";
const StudentAuthContext = createContext(null);

export function StudentAuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", student: null, token: null });

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setState({ status: "anon", student: null, token: null });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const me = await api.studentMe(token);
        if (cancelled) return;
        setState({ status: "auth", student: me, token });
      } catch {
        if (cancelled) return;
        sessionStorage.removeItem(TOKEN_KEY);
        setState({ status: "anon", student: null, token: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const value = {
    ...state,
    setSession: (token, student) => {
      sessionStorage.setItem(TOKEN_KEY, token);
      setState({ status: "auth", student, token });
    },
    setStudent: (student) => setState((p) => ({ ...p, student })),
    logout: () => {
      sessionStorage.removeItem(TOKEN_KEY);
      setState({ status: "anon", student: null, token: null });
    },
  };

  return (
    <StudentAuthContext.Provider value={value}>
      {children}
    </StudentAuthContext.Provider>
  );
}

export function useStudentAuth() {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error("useStudentAuth outside provider");
  return ctx;
}
